import React, { useState, useEffect } from 'react';
import { useApp } from '../../App';
import settingsApi, { DEFAULT_CONTACT_TEAM, padContactTeam } from '../../services/settingsApi';
import { useSiteBrand } from '../../contexts/SiteBrandContext';
import { toast } from 'react-toastify';
import LeadershipAvatar from '../Storefront/LeadershipAvatar';

function LogoUpload({ label, preview, file, setFile, setPreview, inputId }) {
  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
      <div className="flex flex-wrap items-center gap-4">
        {preview && (
          <img
            src={preview}
            alt={label}
            className="h-16 w-auto p-2 border rounded-md bg-slate-50"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <label htmlFor={inputId} className="cursor-pointer bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300">
          Change Logo
        </label>
        <input
          type="file"
          id={inputId}
          className="hidden"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              setFile(f);
              setPreview(URL.createObjectURL(f));
            }
          }}
        />
        {file && <span className="text-xs text-gray-500">Selected: {file.name}</span>}
      </div>
    </div>
  );
}

function SettingsAccordion({ id, title, hint, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <div className="text-lg font-bold text-gray-800">{title}</div>
          {hint ? <div className="text-xs text-gray-500 mt-0.5">{hint}</div> : null}
        </div>
        <span className={`text-gray-400 text-xl leading-none transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open ? <div className="px-5 pb-6 pt-1 border-t border-gray-100">{children}</div> : null}
    </div>
  );
}

function AdminProfileSettingsSection() {
  const ctx = useApp() || {};
  const { refresh: refreshPublicBrand } = useSiteBrand();
  const {
    mainAppLogo, setMainAppLogo,
    appTagline, setAppTagline,
    gstEnabled, setGstEnabled,
    gstRate, setGstRate,
    invoiceTerms, setInvoiceTerms,
    bankDetails, setBankDetails,
    deliveryFromAddress, setDeliveryFromAddress,
    deliveryFooter, setDeliveryFooter,
    slipLogoUrl, setSlipLogoUrl,
    slipWebsiteUrl, setSlipWebsiteUrl,
    slipCompanyPhone, setSlipCompanyPhone,
    slipCompanyMobile, setSlipCompanyMobile,
    slipLogoWidth, setSlipLogoWidth,
    slipLogoHeight, setSlipLogoHeight,
    slipPrinterMode, setSlipPrinterMode,
    slipA4PerPage, setSlipA4PerPage,
    websiteLogo, setWebsiteLogo,
    websiteFooterLogo, setWebsiteFooterLogo,
    websiteLoadingLogo, setWebsiteLoadingLogo,
    userPanelLogo, setUserPanelLogo,
    websiteDomain, setWebsiteDomain,
    websitePhone, setWebsitePhone,
    websiteEmail, setWebsiteEmail,
    websiteWhatsapp, setWebsiteWhatsapp,
    socialFacebook, setSocialFacebook,
    socialInstagram, setSocialInstagram,
    socialYoutube, setSocialYoutube,
    socialTwitter, setSocialTwitter,
    socialWhatsapp, setSocialWhatsapp,
  } = ctx;

  const [localTagline, setLocalTagline] = useState('');
  const [localGstEnabled, setLocalGstEnabled] = useState(true);
  const [localGstRate, setLocalGstRate] = useState(0);
  const [localInvoiceTerms, setLocalInvoiceTerms] = useState('');
  const [localBankDetails, setLocalBankDetails] = useState('');
  const [newMainLogoFile, setNewMainLogoFile] = useState(null);
  const [mainLogoPreview, setMainLogoPreview] = useState('');
  const [localSlipWebsite, setLocalSlipWebsite] = useState('');
  const [localSlipPhone, setLocalSlipPhone] = useState('');
  const [localSlipMobile, setLocalSlipMobile] = useState('');
  const [newSlipLogoFile, setNewSlipLogoFile] = useState(null);
  const [slipLogoPreview, setSlipLogoPreview] = useState('');
  const [localSlipLogoWidth, setLocalSlipLogoWidth] = useState('');
  const [localSlipLogoHeight, setLocalSlipLogoHeight] = useState('');
  const [localFromAddress, setLocalFromAddress] = useState('');
  const [localDeliveryFooter, setLocalDeliveryFooter] = useState('');
  const [localPrinterMode, setLocalPrinterMode] = useState('a4');
  const [localA4PerPage, setLocalA4PerPage] = useState(4);
  const [openSection, setOpenSection] = useState('main');

  const [webLogoFile, setWebLogoFile] = useState(null);
  const [webLogoPreview, setWebLogoPreview] = useState('');
  const [footerLogoFile, setFooterLogoFile] = useState(null);
  const [footerLogoPreview, setFooterLogoPreview] = useState('');
  const [loadingLogoFile, setLoadingLogoFile] = useState(null);
  const [loadingLogoPreview, setLoadingLogoPreview] = useState('');
  const [userLogoFile, setUserLogoFile] = useState(null);
  const [userLogoPreview, setUserLogoPreview] = useState('');
  const [localDomain, setLocalDomain] = useState('');
  const [localPhone, setLocalPhone] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  const [localWhatsapp, setLocalWhatsapp] = useState('');
  const [localFb, setLocalFb] = useState('');
  const [localIg, setLocalIg] = useState('');
  const [localYt, setLocalYt] = useState('');
  const [localTw, setLocalTw] = useState('');
  const [localWaLink, setLocalWaLink] = useState('');
  const [localOfficeTitle, setLocalOfficeTitle] = useState('Visit Our Office');
  const [localOfficeNote, setLocalOfficeNote] = useState('By appointment only');
  const [localOfficeLocation, setLocalOfficeLocation] = useState('Gulberg III, Lahore, Pakistan');
  const [localOfficeMap, setLocalOfficeMap] = useState('https://maps.app.goo.gl/nfMM1mceaq3LndTC6');
  const [localHqTitle, setLocalHqTitle] = useState('Flare Magazine HQ');
  const [localHqNote, setLocalHqNote] = useState('Visit us by appointment');
  const [localHqAddress, setLocalHqAddress] = useState('2nd Floor, Digital Flare Network, Main Boulevard, Gulberg III, Lahore, 54660, Pakistan');
  const [team, setTeam] = useState(DEFAULT_CONTACT_TEAM);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setLocalTagline(appTagline ?? '');
    if (!newMainLogoFile) setMainLogoPreview(mainAppLogo ?? '/Flare Logo.png');
    setLocalGstEnabled(gstEnabled ?? true);
    setLocalGstRate(((gstRate ?? 0.18) * 100));
    setLocalInvoiceTerms(invoiceTerms ?? '');
    setLocalBankDetails(bankDetails ?? '');
    if (!newSlipLogoFile) setSlipLogoPreview(slipLogoUrl ?? '/Flare Logo.png');
    setLocalSlipWebsite(slipWebsiteUrl ?? '');
    setLocalSlipPhone(slipCompanyPhone ?? '');
    setLocalSlipMobile(slipCompanyMobile || '0333-4132462');
    setLocalSlipLogoWidth(slipLogoWidth ?? 20);
    setLocalSlipLogoHeight(slipLogoHeight ?? 20);
    setLocalFromAddress(deliveryFromAddress || 'Flat# 200/14, First Floor Tariq Block Market\nNew Garden Town Lahore.\nPh# 0321-4664602');
    setLocalDeliveryFooter(deliveryFooter || 'Flare Magazine, Lahore\nThank you for subscribing!');
    setLocalPrinterMode(slipPrinterMode || 'a4');
    setLocalA4PerPage(slipA4PerPage ?? 4);
    if (!webLogoFile) setWebLogoPreview(websiteLogo ?? '/Flare Logo.png');
    if (!footerLogoFile) setFooterLogoPreview(websiteFooterLogo ?? '/Flare Logo.png');
    if (!loadingLogoFile) setLoadingLogoPreview(websiteLoadingLogo ?? '/Flare Logo.png');
    if (!userLogoFile) setUserLogoPreview(userPanelLogo ?? '/Flare Logo.png');
    setLocalDomain(websiteDomain ?? 'digital.flare.pk');
    setLocalPhone(websitePhone ?? '');
    setLocalEmail(websiteEmail ?? '');
    setLocalWhatsapp(websiteWhatsapp ?? '');
    setLocalFb(socialFacebook ?? '');
    setLocalIg(socialInstagram ?? '');
    setLocalYt(socialYoutube ?? '');
    setLocalTw(socialTwitter ?? '');
    setLocalWaLink(socialWhatsapp ?? '');
  }, [
    appTagline, mainAppLogo, gstEnabled, gstRate, invoiceTerms, bankDetails,
    slipLogoUrl, slipWebsiteUrl, slipCompanyPhone, slipCompanyMobile, slipLogoWidth, slipLogoHeight, deliveryFromAddress, deliveryFooter,
    slipPrinterMode, slipA4PerPage,
    websiteLogo, websiteFooterLogo, websiteLoadingLogo, userPanelLogo,
    websiteDomain, websitePhone, websiteEmail, websiteWhatsapp,
    socialFacebook, socialInstagram, socialYoutube, socialTwitter, socialWhatsapp,
    newMainLogoFile, newSlipLogoFile, webLogoFile, footerLogoFile, loadingLogoFile, userLogoFile,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await settingsApi.getSettings();
        if (cancelled) return;
        setLocalOfficeTitle(s.officeCardTitle || 'Visit Our Office');
        setLocalOfficeNote(s.officeCardNote || 'By appointment only');
        setLocalOfficeLocation(s.officeCardLocation || 'Gulberg III, Lahore, Pakistan');
        setLocalOfficeMap(s.officeMapUrl || 'https://maps.app.goo.gl/nfMM1mceaq3LndTC6');
        setLocalHqTitle(s.officeHqTitle || 'Flare Magazine HQ');
        setLocalHqNote(s.officeHqNote || 'Visit us by appointment');
        setLocalHqAddress(s.officeHqAddress || '2nd Floor, Digital Flare Network, Main Boulevard, Gulberg III, Lahore, 54660, Pakistan');
        setTeam(padContactTeam(s.contactTeam));
      } catch {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setTeamField = (index, key, value) => {
    setTeam((rows) => rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };

  const onTeamPhoto = (index, file) => {
    if (!file) return;
    setTeam((rows) => rows.map((row, i) => (
      i === index ? { ...row, photoFile: file, photoPreview: URL.createObjectURL(file) } : row
    )));
  };

  const uploadIfNeeded = async (file, field, currentUrl) => {
    if (!file) return currentUrl;
    const up = await settingsApi.uploadImage(field, file);
    toast.info(`${field} uploaded.`);
    return up.displayUrl || up.rawUrl || currentUrl;
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    toast.info('Saving settings to database...');

    try {
      const finalMainLogo = await uploadIfNeeded(newMainLogoFile, 'mainAppLogo', mainAppLogo);
      const finalSlipLogo = await uploadIfNeeded(newSlipLogoFile, 'slipLogoUrl', slipLogoUrl);
      const finalWebLogo = await uploadIfNeeded(webLogoFile, 'websiteLogo', websiteLogo);
      const finalFooterLogo = await uploadIfNeeded(footerLogoFile, 'websiteFooterLogo', websiteFooterLogo);
      const finalLoadingLogo = await uploadIfNeeded(loadingLogoFile, 'websiteLoadingLogo', websiteLoadingLogo);
      const finalUserLogo = await uploadIfNeeded(userLogoFile, 'userPanelLogo', userPanelLogo);

      const people = [];
      for (let i = 0; i < 4; i += 1) {
        const p = team[i] || {};
        const photo = await uploadIfNeeded(p.photoFile, 'contactTeamPhoto', p.photo || '');
        people.push({
          name: (p.name || '').trim(),
          title: (p.title || '').trim(),
          email: (p.email || '').trim(),
          phone: (p.phone || '').trim(),
          photo: photo || '',
        });
      }

      const payload = {
        mainAppLogo: finalMainLogo,
        appTagline: localTagline,
        gstEnabled: localGstEnabled,
        gstRate: parseFloat(localGstRate) / 100,
        invoiceTerms: localInvoiceTerms,
        bankDetails: localBankDetails,
        slipLogoUrl: finalSlipLogo,
        slipWebsiteUrl: localSlipWebsite,
        slipCompanyPhone: localSlipPhone,
        slipCompanyMobile: localSlipMobile.trim() || '0333-4132462',
        slipLogoWidth: parseFloat(localSlipLogoWidth) || 20,
        slipLogoHeight: parseFloat(localSlipLogoHeight) || 20,
        slipPrinterMode: localPrinterMode === 'thermal' ? 'thermal' : 'a4',
        slipA4PerPage: Math.min(4, Math.max(1, parseInt(localA4PerPage, 10) || 4)),
        deliveryFromAddress: localFromAddress,
        deliveryFooter: localDeliveryFooter,
        websiteLogo: finalWebLogo,
        websiteFooterLogo: finalFooterLogo,
        websiteLoadingLogo: finalLoadingLogo,
        userPanelLogo: finalUserLogo,
        websiteDomain: localDomain,
        websitePhone: localPhone,
        websiteEmail: localEmail,
        websiteWhatsapp: localWhatsapp,
        socialFacebook: localFb,
        socialInstagram: localIg,
        socialYoutube: localYt,
        socialTwitter: localTw,
        socialWhatsapp: localWaLink,
        officeCardTitle: localOfficeTitle,
        officeCardNote: localOfficeNote,
        officeCardLocation: localOfficeLocation,
        officeMapUrl: localOfficeMap,
        officeHqTitle: localHqTitle,
        officeHqNote: localHqNote,
        officeHqAddress: localHqAddress,
        contactTeam: people,
      };

      const saved = await settingsApi.saveSettings(payload);
      const apply = (key, setter, fallback) => {
        if (saved?.[key] !== undefined) setter(saved[key]);
        else if (fallback !== undefined) setter(fallback);
      };

      apply('mainAppLogo', setMainAppLogo, finalMainLogo);
      apply('appTagline', setAppTagline, localTagline);
      apply('gstEnabled', setGstEnabled, localGstEnabled);
      apply('gstRate', setGstRate, parseFloat(localGstRate) / 100);
      apply('invoiceTerms', setInvoiceTerms, localInvoiceTerms);
      apply('bankDetails', setBankDetails, localBankDetails);
      apply('deliveryFromAddress', setDeliveryFromAddress, localFromAddress);
      apply('deliveryFooter', setDeliveryFooter, localDeliveryFooter);
      apply('slipLogoUrl', setSlipLogoUrl, finalSlipLogo);
      apply('slipWebsiteUrl', setSlipWebsiteUrl, localSlipWebsite);
      apply('slipCompanyPhone', setSlipCompanyPhone, localSlipPhone);
      apply('slipCompanyMobile', setSlipCompanyMobile, localSlipMobile.trim() || '0333-4132462');
      apply('slipLogoWidth', setSlipLogoWidth, parseFloat(localSlipLogoWidth) || 20);
      apply('slipLogoHeight', setSlipLogoHeight, parseFloat(localSlipLogoHeight) || 20);
      apply('slipPrinterMode', setSlipPrinterMode, localPrinterMode === 'thermal' ? 'thermal' : 'a4');
      apply('slipA4PerPage', setSlipA4PerPage, Math.min(4, Math.max(1, parseInt(localA4PerPage, 10) || 4)));
      apply('websiteLogo', setWebsiteLogo, finalWebLogo);
      apply('websiteFooterLogo', setWebsiteFooterLogo, finalFooterLogo);
      apply('websiteLoadingLogo', setWebsiteLoadingLogo, finalLoadingLogo);
      apply('userPanelLogo', setUserPanelLogo, finalUserLogo);
      apply('websiteDomain', setWebsiteDomain, localDomain);
      apply('websitePhone', setWebsitePhone, localPhone);
      apply('websiteEmail', setWebsiteEmail, localEmail);
      apply('websiteWhatsapp', setWebsiteWhatsapp, localWhatsapp);
      apply('socialFacebook', setSocialFacebook, localFb);
      apply('socialInstagram', setSocialInstagram, localIg);
      apply('socialYoutube', setSocialYoutube, localYt);
      apply('socialTwitter', setSocialTwitter, localTw);
      apply('socialWhatsapp', setSocialWhatsapp, localWaLink);

      if (Array.isArray(saved?.contactTeam)) {
        setTeam(padContactTeam(saved.contactTeam));
      } else {
        setTeam(padContactTeam(people));
      }
      setLocalOfficeTitle(saved?.officeCardTitle || localOfficeTitle);
      setLocalOfficeNote(saved?.officeCardNote || localOfficeNote);
      setLocalOfficeLocation(saved?.officeCardLocation || localOfficeLocation);
      setLocalOfficeMap(saved?.officeMapUrl || localOfficeMap);
      setLocalHqTitle(saved?.officeHqTitle || localHqTitle);
      setLocalHqNote(saved?.officeHqNote || localHqNote);
      setLocalHqAddress(saved?.officeHqAddress || localHqAddress);

      try { await refreshPublicBrand?.(); } catch (_) { /* ignore */ }

      toast.success('All settings saved to MySQL. Website will use new logos/phone/email everywhere.');
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to save settings.';
      toast.error(typeof msg === 'object' ? JSON.stringify(msg) : msg);
    } finally {
      setIsSubmitting(false);
      setNewMainLogoFile(null);
      setNewSlipLogoFile(null);
      setWebLogoFile(null);
      setFooterLogoFile(null);
      setLoadingLogoFile(null);
      setUserLogoFile(null);
    }
  };

  return (
    <div className="container mx-auto">
      <div className="mb-8">
        <h1 className="admin-page-title">Application Settings</h1>
        <p className="admin-page-sub">
          Open one section at a time. Changes save to MySQL and update website / packing slips.
        </p>
      </div>

      <form onSubmit={handleSaveChanges} className="space-y-4">
        <SettingsAccordion
          id="main"
          title="1. Main App Branding"
          hint="Admin panel logo & tagline only"
          openId={openSection}
          setOpenId={setOpenSection}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <LogoUpload label="Application Logo" preview={mainLogoPreview} file={newMainLogoFile} setFile={setNewMainLogoFile} setPreview={setMainLogoPreview} inputId="main-logo-upload" />
            <div>
              <label htmlFor="tagline" className="block text-sm font-bold text-gray-700">Application Tagline</label>
              <input type="text" id="tagline" value={localTagline} onChange={(e) => setLocalTagline(e.target.value)} className="mt-2 block w-full p-3 bg-gray-50 rounded-lg border border-gray-300" />
            </div>
          </div>
        </SettingsAccordion>

        <SettingsAccordion
          id="slip"
          title="2. Delivery Slip Branding & Printer"
          hint="A4 2 or 4 slips/page, or Thermal 4×6 labels"
          openId={openSection}
          setOpenId={setOpenSection}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <LogoUpload label="Delivery Slip Logo" preview={slipLogoPreview} file={newSlipLogoFile} setFile={setNewSlipLogoFile} setPreview={setSlipLogoPreview} inputId="slip-logo-upload" />
              <div>
                <label htmlFor="deliveryFromAddress" className="block text-sm font-bold text-gray-700">FROM Address</label>
                <textarea
                  id="deliveryFromAddress"
                  value={localFromAddress}
                  onChange={(e) => setLocalFromAddress(e.target.value)}
                  rows="3"
                  className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border"
                  placeholder={'Flat# 200/14, First Floor Tariq Block Market\nNew Garden Town Lahore.\nPh# 0321-4664602'}
                />
                <p className="text-xs text-gray-500 mt-1">Line 1: street address. Line 2: phone (Ph# ...).</p>
              </div>
              <div>
                <label htmlFor="deliveryFooter" className="block text-sm font-bold text-gray-700">Footer Text</label>
                <textarea
                  id="deliveryFooter"
                  value={localDeliveryFooter}
                  onChange={(e) => setLocalDeliveryFooter(e.target.value)}
                  rows="3"
                  className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border"
                  placeholder={'Flare Magazine, Lahore\nThank you for subscribing!'}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="slipWebsite" className="block text-sm font-bold text-gray-700">Website URL / Domain (under logo)</label>
                <input type="text" id="slipWebsite" value={localSlipWebsite} onChange={(e) => setLocalSlipWebsite(e.target.value)} className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border" placeholder="digital.flare.pk" />
              </div>
              <div>
                <label htmlFor="slipPhone" className="block text-sm font-bold text-gray-700">Helpline (landline)</label>
                <input type="text" id="slipPhone" value={localSlipPhone} onChange={(e) => setLocalSlipPhone(e.target.value)} className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border" placeholder="042-111-123-456" />
              </div>
              <div>
                <label htmlFor="slipMobile" className="block text-sm font-bold text-gray-700">Mobile (for slips)</label>
                <input type="text" id="slipMobile" value={localSlipMobile} onChange={(e) => setLocalSlipMobile(e.target.value)} className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border" placeholder="0333-4132462" />
                <p className="text-xs text-gray-500 mt-1">Printed under Helpline on packing slips.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="slipLogoWidth" className="block text-sm font-bold text-gray-700">Logo Width (mm)</label>
                  <input type="number" id="slipLogoWidth" value={localSlipLogoWidth} onChange={(e) => setLocalSlipLogoWidth(e.target.value)} className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border" />
                </div>
                <div>
                  <label htmlFor="slipLogoHeight" className="block text-sm font-bold text-gray-700">Logo Height (mm)</label>
                  <input type="number" id="slipLogoHeight" value={localSlipLogoHeight} onChange={(e) => setLocalSlipLogoHeight(e.target.value)} className="mt-1 block w-full p-2 bg-gray-50 rounded-lg border" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 p-5 rounded-xl border border-rose-100 bg-rose-50/40 space-y-4">
            <h4 className="text-base font-bold text-gray-800">Printer Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Printer Mode</label>
                <select
                  value={localPrinterMode}
                  onChange={(e) => setLocalPrinterMode(e.target.value)}
                  className="w-full p-3 bg-white rounded-lg border border-gray-300 font-semibold"
                >
                  <option value="a4">A4 paper</option>
                  <option value="thermal">Thermal label — 4×6&quot; (100×150 mm), 1 slip / label</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">A4 slips per page</label>
                <select
                  value={localA4PerPage}
                  onChange={(e) => setLocalA4PerPage(e.target.value)}
                  disabled={localPrinterMode !== 'a4'}
                  className="w-full p-3 bg-white rounded-lg border border-gray-300 font-semibold disabled:opacity-50"
                >
                  <option value={2}>2 slips (stacked) — larger professional type</option>
                  <option value={4}>4 slips (2×2 grid) — compact type</option>
                  <option value={1}>1 slip — full page</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {localPrinterMode !== 'a4'
                    ? 'Print dialog: paper 4×6 in (or 100×150 mm), margins None, scale 100%.'
                    : Number(localA4PerPage) >= 4
                      ? 'Compact type for a 2×2 grid. Print: A4 Portrait, Actual size / 100%.'
                      : Number(localA4PerPage) === 1
                        ? 'Full-page slip with large type. Print: A4 Portrait, Actual size / 100%.'
                        : 'Two stacked slips with larger professional type. Print: A4 Portrait, Actual size / 100%.'}
                </p>
              </div>
            </div>
          </div>
        </SettingsAccordion>

        <SettingsAccordion
          id="website"
          title="3. Website & User Panel Branding"
          hint="Logos, phone, email, social — whole site except admin"
          openId={openSection}
          setOpenId={setOpenSection}
        >
          <p className="text-sm text-gray-500 mb-6">
            These logos, phone, email and social links apply across the public website and customer account panel. Admin panel logo stays separate (section 1).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <LogoUpload label="Website Header Logo" preview={webLogoPreview} file={webLogoFile} setFile={setWebLogoFile} setPreview={setWebLogoPreview} inputId="web-logo-upload" />
            <LogoUpload label="Website Footer Logo" preview={footerLogoPreview} file={footerLogoFile} setFile={setFooterLogoFile} setPreview={setFooterLogoPreview} inputId="footer-logo-upload" />
            <LogoUpload label="Loading Screen Logo" preview={loadingLogoPreview} file={loadingLogoFile} setFile={setLoadingLogoFile} setPreview={setLoadingLogoPreview} inputId="loading-logo-upload" />
            <LogoUpload label="User Panel Logo" preview={userLogoPreview} file={userLogoFile} setFile={setUserLogoFile} setPreview={setUserLogoPreview} inputId="user-logo-upload" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700">Website Domain Label</label>
              <input type="text" value={localDomain} onChange={(e) => setLocalDomain(e.target.value)} placeholder="digital.flare.pk" className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Public Phone Number</label>
              <input type="text" value={localPhone} onChange={(e) => setLocalPhone(e.target.value)} placeholder="042-111-123-456" className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Public Email</label>
              <input type="email" value={localEmail} onChange={(e) => setLocalEmail(e.target.value)} placeholder="info@digital.flare.pk" className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">WhatsApp Number (digits, with country code)</label>
              <input type="text" value={localWhatsapp} onChange={(e) => setLocalWhatsapp(e.target.value)} placeholder="9242111123456" className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
          </div>
          <h4 className="text-lg font-bold text-gray-700 mt-8 mb-4">Social Links</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700">Facebook URL</label>
              <input type="url" value={localFb} onChange={(e) => setLocalFb(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Instagram URL</label>
              <input type="url" value={localIg} onChange={(e) => setLocalIg(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="https://instagram.com/..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">YouTube URL</label>
              <input type="url" value={localYt} onChange={(e) => setLocalYt(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="https://youtube.com/..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Twitter / X URL</label>
              <input type="url" value={localTw} onChange={(e) => setLocalTw(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="https://x.com/..." />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700">WhatsApp Chat Link (optional override)</label>
              <input type="url" value={localWaLink} onChange={(e) => setLocalWaLink(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="https://wa.me/92..." />
            </div>
          </div>

          <h4 className="text-lg font-bold text-gray-700 mt-10 mb-1">Contact page — Office &amp; map</h4>
          <p className="text-sm text-gray-500 mb-4">Shows on /contact: Visit Our Office card, HQ overlay, and Google Map.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700">Visit Our Office (card title)</label>
              <input type="text" value={localOfficeTitle} onChange={(e) => setLocalOfficeTitle(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Card note</label>
              <input type="text" value={localOfficeNote} onChange={(e) => setLocalOfficeNote(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="By appointment only" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Short location (card)</label>
              <input type="text" value={localOfficeLocation} onChange={(e) => setLocalOfficeLocation(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="Gulberg III, Lahore, Pakistan" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">Google Map link</label>
              <input type="url" value={localOfficeMap} onChange={(e) => setLocalOfficeMap(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" placeholder="https://maps.app.goo.gl/..." />
              <p className="text-xs text-gray-500 mt-1">Paste a Google Maps share URL or an address. Used for the card and the map.</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">HQ title (map overlay)</label>
              <input type="text" value={localHqTitle} onChange={(e) => setLocalHqTitle(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700">HQ note</label>
              <input type="text" value={localHqNote} onChange={(e) => setLocalHqNote(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700">Full HQ address</label>
              <textarea value={localHqAddress} onChange={(e) => setLocalHqAddress(e.target.value)} rows="2" className="mt-1 block w-full p-3 bg-gray-50 rounded-lg border" />
            </div>
          </div>

          <h4 className="text-lg font-bold text-gray-700 mt-10 mb-1">Contact page — Leadership (4 people)</h4>
          <p className="text-sm text-gray-500 mb-4">Photo, name, designation, email, phone. Empty name is hidden on the website.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {team.map((p, i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <LeadershipAvatar
                    index={i}
                    name={p.name}
                    photo={p.photoPreview || p.photo}
                    className="h-14 w-14"
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-black uppercase tracking-widest text-gray-400">Person {i + 1}</div>
                    <label className="text-sm font-bold text-red-700 cursor-pointer">
                      Change photo
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onTeamPhoto(i, e.target.files?.[0])}
                      />
                    </label>
                  </div>
                </div>
                <input type="text" value={p.name} onChange={(e) => setTeamField(i, 'name', e.target.value)} placeholder="Full name" className="block w-full p-2.5 bg-white rounded-lg border" />
                <input type="text" value={p.title} onChange={(e) => setTeamField(i, 'title', e.target.value)} placeholder="Designation (CEO, Director…)" className="block w-full p-2.5 bg-white rounded-lg border" />
                <input type="email" value={p.email} onChange={(e) => setTeamField(i, 'email', e.target.value)} placeholder="Email" className="block w-full p-2.5 bg-white rounded-lg border" />
                <input type="text" value={p.phone} onChange={(e) => setTeamField(i, 'phone', e.target.value)} placeholder="Contact number" className="block w-full p-2.5 bg-white rounded-lg border" />
              </div>
            ))}
          </div>
        </SettingsAccordion>

        <SettingsAccordion
          id="finance"
          title="4. Financial & Invoice Settings"
          hint="GST, invoice terms, bank details"
          openId={openSection}
          setOpenId={setOpenSection}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl border space-y-4">
              <h4 className="text-lg font-bold text-gray-700 mb-2">Financial Settings</h4>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input type="checkbox" checked={!!localGstEnabled} onChange={(e) => setLocalGstEnabled(e.target.checked)} className="h-5 w-5 rounded text-red-600 focus:ring-red-500" />
                <span className="font-medium text-gray-700">Enable GST on Invoices</span>
              </label>
              {localGstEnabled && (
                <div>
                  <label htmlFor="gstRate" className="block text-sm font-medium text-gray-700">GST Rate (%)</label>
                  <input type="number" id="gstRate" step="0.01" value={localGstRate} onChange={(e) => setLocalGstRate(e.target.value)} className="mt-1 block w-full p-3 bg-white rounded-lg border border-gray-300" />
                </div>
              )}
            </div>
            <div className="space-y-6">
              <div>
                <label htmlFor="invoiceTerms" className="block text-sm font-bold text-gray-700">Invoice Terms & Conditions</label>
                <textarea id="invoiceTerms" value={localInvoiceTerms} onChange={(e) => setLocalInvoiceTerms(e.target.value)} rows="4" className="mt-2 block w-full p-3 bg-gray-50 rounded-lg border border-gray-300" />
              </div>
              <div>
                <label htmlFor="bankDetails" className="block text-sm font-bold text-gray-700">Bank Account Details</label>
                <textarea id="bankDetails" value={localBankDetails} onChange={(e) => setLocalBankDetails(e.target.value)} rows="4" className="mt-2 block w-full p-3 bg-gray-50 rounded-lg border border-gray-300" />
              </div>
            </div>
          </div>
        </SettingsAccordion>

        <div className="pt-2 flex justify-end">
          <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto text-white font-bold py-3 px-10 rounded-lg shadow-lg disabled:opacity-50" style={{ backgroundColor: '#c4151c' }}>
            {isSubmitting ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminProfileSettingsSection;
